import { FC, useEffect, useState } from 'react';
import { Modal, Input, Form, Alert, Divider, Spin, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { NAMESPACE, TAG } from '@/constants';
import { mediaPlanApi, useUpdatePoNumberMutation, useGetChannelListQuery } from '@/api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { pandaDocErrorMessage } from '@/utils/pandaDoc';

interface Props {
  open: boolean;
  onClose: () => void;
  mediaPlanId: string;
  /** Current plan-level PO, so the field opens pre-filled. */
  poNumber?: string;
}

/**
 * Records PO numbers against a media plan, including after its IO is signed.
 *
 * Clients frequently supply a PO days or weeks after signing, so this is a
 * routine billing step rather than a correction. When the plan is already
 * signed the backend re-syncs booking data, and we say so — that is the signal
 * the number has reached the data finance bills from.
 *
 * POs are set per line item as well as for the plan, because a client can
 * raise one per channel. A line's own number wins where it has one; the
 * plan-level field covers the common case of a single PO for everything, so
 * nobody has to type the same number into every line.
 */
export const PoNumberModal: FC<Props> = ({ open, onClose, mediaPlanId, poNumber }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const dispatch = useAppDispatch();
  const [updatePoNumber, { isLoading }] = useUpdatePoNumberMutation();
  const [value, setValue] = useState(poNumber ?? '');
  const [error, setError] = useState<string | null>(null);
  /** Per-line edits, keyed by line id. Only what changed is sent. */
  const [lineValues, setLineValues] = useState<Record<string, string>>({});

  const { data: lineItems, isFetching: areLinesFetching } = useGetChannelListQuery(
    { mediaPlan: mediaPlanId, limit: 100 } as never,
    { skip: !open || !mediaPlanId }
  );

  // Re-seed whenever the modal is reopened, so a cancelled edit isn't retained.
  useEffect(() => {
    if (open) {
      setValue(poNumber ?? '');
      setError(null);
    }
  }, [open, poNumber]);

  const handleSave = async () => {
    setError(null);
    try {
      /* Plan-level first, then each changed line. Sequential rather than
         parallel: every call re-syncs the plan's bookings, and several at once
         would race to write the same generation. */
      const { synced } = await updatePoNumber({ mediaPlanId, poNumber: value.trim() }).unwrap();

      for (const [lineItemId, lineValue] of Object.entries(lineValues)) {
        await updatePoNumber({ mediaPlanId, lineItemId, poNumber: lineValue.trim() }).unwrap();
      }
      /* The plan list is what pre-fills this modal, and it lives in a separate
         API slice the mutation's own tags cannot reach — left cached, reopening
         showed the old PO, and saving again put it back. */
      dispatch(mediaPlanApi.util.invalidateTags([TAG.MEDIA_PLAN]));
      message.success(synced ? t('poSavedAndSynced') : t('poSaved'));
      setLineValues({});
      onClose();
    } catch (err) {
      setError(
        pandaDocErrorMessage(err, {
          sessionExpired: t('ioSessionExpired'),
          integrationUnavailable: t('ioIntegrationUnavailable'),
          fallback: t('poSaveFailed')
        })
      );
    }
  };

  return (
    <Modal
      open={open}
      title={t('poModalTitle')}
      onCancel={onClose}
      onOk={handleSave}
      okText={t('save', { ns: NAMESPACE.COMMON })}
      cancelText={t('cancel', { ns: NAMESPACE.COMMON })}
      confirmLoading={isLoading}
      okButtonProps={{ disabled: isLoading }}
      destroyOnHidden
    >
      {error && <Alert type="error" role="alert" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form layout="vertical">
        <Form.Item label={t('poNumberLabel')} htmlFor="po-number-input" help={t('poNumberHelp')}>
          <Input
            id="po-number-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('poNumberPlaceholder')}
            onPressEnter={handleSave}
            allowClear
            autoFocus
          />
        </Form.Item>

        {/* Per-line fields below the plan-level one, so the common case — a
            single PO for the whole campaign — is still the first thing on
            screen and needs no scrolling past a list of channels. Each box is
            placeholdered with the plan's number, showing what the line
            inherits when left blank rather than implying it has none. */}
        {Boolean(lineItems?.data?.length) && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('poPerLineHint')}
            </Typography.Text>

            <Spin spinning={areLinesFetching}>
              {(lineItems?.data ?? []).map((line) => (
                <Form.Item
                  key={line._id}
                  label={[line.channel, line.product].filter(Boolean).join(' — ')}
                  style={{ marginBottom: 8, marginTop: 8 }}
                >
                  <Input
                    value={lineValues[line._id] ?? line.poNumber ?? ''}
                    placeholder={value.trim() || t('poNumberPlaceholder')}
                    onChange={(event) =>
                      setLineValues((current) => ({
                        ...current,
                        [line._id]: event.target.value
                      }))
                    }
                  />
                </Form.Item>
              ))}
            </Spin>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default PoNumberModal;
